import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { Loader2 } from "lucide-react";

import { useIsAdmin } from "~/entities/admin";
import type { UserSearchItem } from "~/entities/user";
import { Button } from "~/shared/ui/button";
import { DateTimePicker } from "~/shared/ui/date-time-picker";
import { Label } from "~/shared/ui/label";

import {
    useAttribution,
    useUpdateAttribution,
    type AttributionCreator,
    type AttributionEntity,
    type AttributionUpdate,
} from "./api";
import { CreatorPicker } from "./creator-picker";

const toDate = (value: string | null): Date | null => (value ? new Date(value) : null);

export interface SuperadminAttributionFieldsProps {
    entity: AttributionEntity;
    entityId: string | undefined;
    /** Дёргается после успешного сохранения — родителю пора обновить свои списки. */
    onSaved?: () => void;
    className?: string;
}

/**
 * Привилегированный блок «создатель + дата создания».
 *
 * Переходный инструмент до эпика RBAC: рендерится только суперадмину и только
 * когда бэкенд разрешил правку (`editable`) — в личном пространстве блока нет
 * вовсе. Сохраняется отдельно от формы-хозяина: авторство живёт на своём роуте,
 * поэтому обычная форма переименования продолжает работать как раньше.
 */
export function SuperadminAttributionFields({
    entity,
    entityId,
    onSaved,
    className,
}: SuperadminAttributionFieldsProps) {
    const { t } = useTranslation("admin");
    const { data: isSuperAdmin = false } = useIsAdmin();
    const { data } = useAttribution(entity, entityId, { enabled: isSuperAdmin });
    const update = useUpdateAttribution(entity, entityId);

    const [creator, setCreator] = useState<AttributionCreator | null>(null);
    const [createdAt, setCreatedAt] = useState<Date | null>(null);

    // Сервер — источник истины: любой ответ (загрузка, своё же сохранение,
    // рефетч) переустанавливает поля, чтобы не показывать несохранённый черновик.
    useEffect(() => {
        if (!data) return;
        setCreator(data.creator);
        setCreatedAt(toDate(data.createdAt));
    }, [data]);

    if (!isSuperAdmin || !data?.editable) return null;

    const min = toDate(data.minCreatedAt);
    const max = toDate(data.maxCreatedAt) ?? new Date();

    const creatorChanged = !!creator && creator.userId !== data.creator?.userId;
    const dateChanged =
        !!createdAt && createdAt.getTime() !== (toDate(data.createdAt)?.getTime() ?? Number.NaN);
    const dirty = creatorChanged || dateChanged;

    const handleSelect = (user: UserSearchItem) => {
        setCreator({ userId: user.id, email: user.email, name: user.name, image: user.image });
    };

    const handleSave = () => {
        // Пустой патч бэкенд отвергает — шлём только реально изменённые поля.
        const payload: AttributionUpdate = {
            ...(creatorChanged && creator ? { creatorId: creator.userId } : {}),
            ...(dateChanged && createdAt ? { createdAt: createdAt.toISOString() } : {}),
        };
        update.mutate(payload, {
            onSuccess: () => {
                toast.success(t("attribution.saved"));
                onSaved?.();
            },
            onError: () => toast.error(t("attribution.saveError")),
        });
    };

    return (
        <div className={className}>
            <div className="flex flex-col gap-3 rounded-md border border-dashed p-3">
                <p className="text-xs font-medium text-muted-foreground">{t("attribution.title")}</p>

                <div className="flex flex-col gap-1.5">
                    <Label htmlFor="attribution-creator">{t("attribution.creator")}</Label>
                    <CreatorPicker
                        id="attribution-creator"
                        t={t}
                        value={creator}
                        onSelect={handleSelect}
                        disabled={update.isPending}
                    />
                </div>

                <div className="flex flex-col gap-1.5">
                    <Label htmlFor="attribution-created-at">{t("attribution.createdAt")}</Label>
                    <DateTimePicker
                        id="attribution-created-at"
                        value={createdAt}
                        onChange={setCreatedAt}
                        min={min}
                        max={max}
                        disabled={update.isPending}
                    />
                    <p className="text-[10px] text-muted-foreground">
                        {min
                            ? t("attribution.rangeBoth", {
                                  from: min.toLocaleString(),
                                  to: max.toLocaleString(),
                              })
                            : t("attribution.rangeUntil", { to: max.toLocaleString() })}
                    </p>
                </div>

                <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="self-end"
                    disabled={!dirty || update.isPending}
                    onClick={handleSave}
                >
                    {update.isPending ? <Loader2 className="size-3.5 animate-spin" aria-hidden /> : null}
                    {t("attribution.save")}
                </Button>
            </div>
        </div>
    );
}
